import { z } from 'zod';
import { defineEndpoint, type PermissionString } from '../contract.ts';
import { GuildChannel, GuildRole } from '../discord/base.ts';
import { defineEvent } from '../events.ts';

// Module settings (design §6.9) and module switches. Each module's settings are
// a zod schema in its manifest; the admin panel builds a form from the JSON Schema
// these endpoints return. Every change is validated, compare-and-set, audited and
// published as core.settings.changed.

/** Published after every settings change. Subscribers re-read the module's settings. */
export const SettingsChanged = defineEvent({
  type: 'core.settings.changed',
  data: z.object({ module: z.string(), version: z.number().int() }),
});

export const ModuleSettings = z.object({
  module: z.string(),
  /** The current values: what admins have set, over the schema's defaults. */
  values: z.record(z.string(), z.unknown()),
  version: z.number().int(),
  /** The settings schema as JSON Schema, with defaults and descriptions. */
  schema: z.record(z.string(), z.unknown()),
  /** The display name of the admin who saved them last, or null if no admin has. */
  updatedBy: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ModuleSettings = z.infer<typeof ModuleSettings>;

/**
 * The settings contracts for one module: `GET` and `PUT /api/admin/settings/<name>`,
 * both requiring `permission` (the module's `<name>.admin`). Core registers one pair
 * per module that has settings.
 */
export function moduleSettingsEndpoints(name: string, permission: PermissionString) {
  return {
    get: defineEndpoint({
      method: 'GET',
      path: `/admin/settings/${name}`,
      permission,
      response: ModuleSettings,
    }),
    put: defineEndpoint({
      method: 'PUT',
      path: `/admin/settings/${name}`,
      permission,
      recentLogin: true,
      /** The complete new values; omitted keys take their defaults. `version` is the one the editor loaded. */
      body: z.object({ version: z.number().int(), values: z.record(z.string(), z.unknown()) }),
      response: ModuleSettings,
    }),
    /** The guild's roles and channels for the form's pickers (plan 1C Assumption 15). 503 without a worker. */
    discord: defineEndpoint({
      method: 'GET',
      path: `/admin/settings/${name}/discord`,
      permission,
      response: z.object({ roles: z.array(GuildRole), channels: z.array(GuildChannel), fetchedAt: z.iso.datetime() }),
    }),
  };
}

export const ModuleInfo = z.object({
  name: z.string(),
  title: z.string(),
  enabled: z.boolean(),
  /** Whether it has a settings form; its settings permission is `<name>.admin`. */
  hasSettings: z.boolean(),
});
export type ModuleInfo = z.infer<typeof ModuleInfo>;

/** Every module in this build, with whether it's enabled here. Any logged-in user, so the shell can build its nav. */
export const listModules = defineEndpoint({
  method: 'GET',
  path: '/admin/modules',
  permission: 'authenticated',
  response: z.object({ items: z.array(ModuleInfo) }),
});
